import moment from "moment";

export const SYNC_FREEZE_DURATION_MS = 20 * 60 * 1000; // per-row local optimistic freeze fallback
export const DASHBOARD_SYNC_FREEZE_DURATION_MS = 60 * 60 * 1000; // bulk/dashboard local optimistic freeze fallback

const storageKey = (key) => `sync_freeze_${key}`;

export const getSyncFrozenUntil = (key) =>
  Number(localStorage.getItem(storageKey(key)) || 0);

export const setSyncFrozenNow = (key, durationMs = SYNC_FREEZE_DURATION_MS) => {
  const frozenUntil = Date.now() + durationMs;
  localStorage.setItem(storageKey(key), String(frozenUntil));
  return frozenUntil;
};

export const formatSyncCountdown = (ms) => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

// Same as formatSyncCountdown but rolls over into an hours segment, for the
// longer (1 hour) bulk/dashboard cooldown.
export const formatDashboardCountdown = (ms) => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return hours > 0
    ? `${hours}:${minutes.toString().padStart(2, "0")}:${seconds
        .toString()
        .padStart(2, "0")}`
    : `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

export const formatLastSynced = (lastSynced) =>
  lastSynced && moment(lastSynced).isValid()
    ? moment(lastSynced).format("DD MMM YYYY, hh:mm A")
    : "--";

const remainingFromLastSynced = (lastSynced, coolDownMs) => {
  const lastSyncedMs = lastSynced ? new Date(lastSynced).getTime() : NaN;
  return Number.isNaN(lastSyncedMs) ? 0 : lastSyncedMs + coolDownMs - Date.now();
};

// Per-row sync buttons on list screens (Billing, Return Bill, Purchase Order,
// Purchase Invoice, Purchase Return). Backend sends data_synced + last_synced per record:
// - data_synced true  -> permanently disabled, label shows the last synced date/time.
// - data_synced false -> disabled for `coolDownMs` (20 min) counted from last_synced; a local
//   optimistic freeze (set right after a click) bridges the gap until the list refetches.
export const getRowSyncState = ({
  moduleKey,
  id,
  dataSynced,
  lastSynced,
  coolDownMs = SYNC_FREEZE_DURATION_MS,
}) => {
  if (dataSynced) {
    return { disabled: true, synced: true, label: formatLastSynced(lastSynced) };
  }
  const serverRemainingMs = remainingFromLastSynced(lastSynced, coolDownMs);
  const localRemainingMs = getSyncFrozenUntil(`${moduleKey}_${id}`) - Date.now();
  const remainingMs = Math.max(serverRemainingMs, localRemainingMs);
  return {
    disabled: remainingMs > 0,
    synced: false,
    remainingMs,
    label: remainingMs > 0 ? formatSyncCountdown(remainingMs) : "Sync",
  };
};

// Bulk/dashboard sync buttons (Sync Todays Bills, Sync Dashboard). Driven by
// GET /sync-dashboard/timings -> { type, last_synced }, with a 1 hour cooldown.
export const getBulkSyncState = ({
  moduleKey,
  lastSynced,
  coolDownMs = DASHBOARD_SYNC_FREEZE_DURATION_MS,
}) => {
  const serverRemainingMs = remainingFromLastSynced(lastSynced, coolDownMs);
  const localRemainingMs = moduleKey
    ? getSyncFrozenUntil(moduleKey) - Date.now()
    : 0;
  const remainingMs = Math.max(serverRemainingMs, localRemainingMs);
  return {
    disabled: remainingMs > 0,
    remainingMs,
    lastSyncedLabel: formatLastSynced(lastSynced),
  };
};
